// Hook for AccountPickerTree using IndexedDB local data
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuthStore } from '@/app/store/authStore';
import { useSQLiteSync } from '@/hooks/useSQLiteSync';
import {
  getAllAccountTypesAsync,
  getAllAccountGroupsAsync,
  getAllAccountsAsync,
  getAccountByIdAsync,
  hasAccountsAsync,
  LocalAccountType,
  LocalAccountGroup,
  LocalAccount
} from '@/lib/indexeddb/database';
import { isSQLiteAvailable } from '@/lib/indexeddb/integrationUtils';
import { TreeNode } from '@/app/pages/accounting/chart-of-accounts/components/treeTypes';
import {
  getVisibleNodes,
  getAncestorIds,
} from '@/app/pages/accounting/chart-of-accounts/components/treeUtils';

export interface AccountPickerTreeFilters {
  filterAccountNature?: 'debit' | 'credit';
  filterAccountTypeIds?: number[];
  filterAccountGroupIds?: number[];
  filterAccountIds?: number[];
  showInactiveAccounts?: boolean;
}

export interface UseAccountPickerTreeOptions extends AccountPickerTreeFilters {
  selectedAccountId?: number | null;
}

export interface UseAccountPickerTreeResult {
  // Data
  visibleNodes: TreeNode[];
  allNodes: TreeNode[];

  // Loading states
  isLoading: boolean;
  isLoadingInitial: boolean;

  // Error state
  error: Error | null;

  // Expansion state
  expandedIds: Set<string>;
  toggleExpand: (nodeId: string) => void;
  expandToNode: (nodeId: string) => void;

  // Search state
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  // Selected account for display
  selectedAccount: LocalAccount | null;

  // Sync state
  needsSync: boolean;
  isSyncing: boolean;
  syncData: () => Promise<void>;

  // Actions
  refreshData: () => void;
}

// Build tree nodes from local data
function buildTreeNodesFromLocal(
  accountTypes: LocalAccountType[],
  accountGroups: LocalAccountGroup[],
  accounts: LocalAccount[]
): TreeNode[] {
  const nodes: TreeNode[] = [];

  // Index groups by account_type_id and parent_id
  const groupsByTypeId = new Map<number, LocalAccountGroup[]>();
  accountGroups.forEach(group => {
    const byType = groupsByTypeId.get(group.account_type_id) || [];
    byType.push(group);
    groupsByTypeId.set(group.account_type_id, byType);
  });

  // Index accounts by group_id
  const accountsByGroupId = new Map<number, LocalAccount[]>();
  accounts.forEach(account => {
    const byGroup = accountsByGroupId.get(account.account_group_id) || [];
    byGroup.push(account);
    accountsByGroupId.set(account.account_group_id, byGroup);
  });

  // Build tree recursively
  function addGroupNode(group: LocalAccountGroup, level: number, parentNodeId?: string) {
    const groupNodeId = `group-${group.id}`;
    const childGroups = accountGroups.filter(g => g.parent_id === group.id);
    const groupAccounts = accountsByGroupId.get(group.id) || [];

    nodes.push({
      id: groupNodeId,
      nodeType: 'account_group',
      name: group.name,
      code: group.code,
      level,
      isActive: group.is_active,
      hasChildren: childGroups.length > 0 || groupAccounts.length > 0,
      rawId: group.id,
      parentNodeId,
      accountTypeId: group.account_type_id,
    });

    // Add child groups recursively
    childGroups
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(childGroup => {
        addGroupNode(childGroup, level + 1, groupNodeId);
      });

    // Add accounts under this group
    groupAccounts
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(account => {
        nodes.push({
          id: `account-${account.id}`,
          nodeType: 'account',
          name: account.name,
          code: account.code,
          level: level + 1,
          isActive: account.is_active,
          hasChildren: false,
          rawId: account.id,
          parentNodeId: groupNodeId,
          accountGroupId: account.account_group_id,
        });
      });
  }

  // Sort account types by name
  const sortedTypes = [...accountTypes].sort((a, b) => a.name.localeCompare(b.name));

  sortedTypes.forEach(type => {
    const typeNodeId = `type-${type.id}`;

    // Get root groups for this type (no parent)
    const rootGroups = accountGroups.filter(
      g => g.account_type_id === type.id && !g.parent_id
    );

    nodes.push({
      id: typeNodeId,
      nodeType: 'account_type',
      name: type.name,
      level: 0,
      nature: type.nature,
      hasChildren: rootGroups.length > 0,
      rawId: type.id,
    });

    // Add root groups and their children
    rootGroups
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(group => {
        addGroupNode(group, 1, typeNodeId);
      });
  });

  return nodes;
}

// Filter nodes by search query
function filterNodesBySearchLocal(
  allNodes: TreeNode[],
  searchQuery: string,
  showInactive: boolean
): TreeNode[] {
  const query = searchQuery.toLowerCase().trim();

  if (!query && showInactive) {
    return allNodes;
  }

  const matchingIds = new Set<string>();
  const ancestorIds = new Set<string>();

  // First pass: find matching nodes
  for (const node of allNodes) {
    // Filter inactive if needed
    if (!showInactive && node.isActive === false) {
      continue;
    }

    // Check search match
    if (query) {
      const nameMatch = node.name.toLowerCase().includes(query);
      const codeMatch = node.code?.toLowerCase().includes(query);

      if (nameMatch || codeMatch) {
        matchingIds.add(node.id);
        // Add all ancestors to ensure they're visible
        getAncestorIds(node.id, allNodes).forEach(id => ancestorIds.add(id));
      }
    } else {
      matchingIds.add(node.id);
    }
  }

  // Include matching nodes and their ancestors
  return allNodes.filter(node => {
    if (!showInactive && node.isActive === false) {
      return false;
    }
    return matchingIds.has(node.id) || ancestorIds.has(node.id);
  });
}

export function useAccountPickerTree(options: UseAccountPickerTreeOptions = {}): UseAccountPickerTreeResult {
  const {
    filterAccountNature,
    filterAccountTypeIds,
    filterAccountGroupIds,
    filterAccountIds,
    showInactiveAccounts = false,
    selectedAccountId,
  } = options;

  const userOrgId = useAuthStore(state => state.userOrgId);

  // IndexedDB sync hook
  const {
    isInitialized: isIndexedDBInitialized,
    isSyncing,
    syncData: syncDataFn,
    lastSyncTime,
  } = useSQLiteSync();

  // State
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAccount, setSelectedAccount] = useState<LocalAccount | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [needsSync, setNeedsSync] = useState(false);

  // Local data state
  const [accountTypes, setAccountTypes] = useState<LocalAccountType[]>([]);
  const [accountGroups, setAccountGroups] = useState<LocalAccountGroup[]>([]);
  const [accounts, setAccounts] = useState<LocalAccount[]>([]);

  // Load data from IndexedDB
  const loadLocalData = useCallback(async () => {
    if (!isIndexedDBInitialized || !userOrgId || !isSQLiteAvailable()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Check if we have accounts synced
      const hasAccounts = await hasAccountsAsync(userOrgId);
      if (!hasAccounts) {
        setNeedsSync(true);
        setIsLoading(false);
        return;
      }

      setNeedsSync(false);

      // Load all data from IndexedDB
      const [types, groups, accts] = await Promise.all([
        getAllAccountTypesAsync(),
        getAllAccountGroupsAsync(userOrgId),
        getAllAccountsAsync(userOrgId),
      ]);

      console.log(`[useAccountPickerTree] Loaded ${types.length} types, ${groups.length} groups, ${accts.length} accounts from IndexedDB`);

      setAccountTypes(types);
      setAccountGroups(groups);
      setAccounts(accts);
    } catch (err) {
      console.error('[useAccountPickerTree] Error loading local data:', err);
      setError(err instanceof Error ? err : new Error('Failed to load local data'));
    } finally {
      setIsLoading(false);
    }
  }, [isIndexedDBInitialized, userOrgId]);

  // Load data when initialized or after sync
  useEffect(() => {
    loadLocalData();
  }, [loadLocalData, lastSyncTime]);

  // Load selected account for display
  useEffect(() => {
    if (selectedAccountId && isIndexedDBInitialized) {
      getAccountByIdAsync(selectedAccountId)
        .then(account => setSelectedAccount(account || null))
        .catch(() => setSelectedAccount(null));
    } else {
      setSelectedAccount(null);
    }
  }, [selectedAccountId, isIndexedDBInitialized]);

  // Apply filters to account types
  const filteredAccountTypes = useMemo(() => {
    let result = accountTypes;

    if (filterAccountNature) {
      result = result.filter(t => t.nature === filterAccountNature);
    }

    if (filterAccountTypeIds && filterAccountTypeIds.length > 0) {
      result = result.filter(t => filterAccountTypeIds.includes(t.id));
    }

    return result;
  }, [accountTypes, filterAccountNature, filterAccountTypeIds]);

  // Apply filters to account groups
  const filteredAccountGroups = useMemo(() => {
    let result = accountGroups;

    // Filter by account type
    if (filterAccountNature || (filterAccountTypeIds && filterAccountTypeIds.length > 0)) {
      const validTypeIds = new Set(filteredAccountTypes.map(t => t.id));
      result = result.filter(g => validTypeIds.has(g.account_type_id));
    }

    if (filterAccountGroupIds && filterAccountGroupIds.length > 0) {
      const filterSet = new Set(filterAccountGroupIds);
      const ancestorSet = new Set<number>();

      // Find all ancestors of filtered groups
      filterAccountGroupIds.forEach(groupId => {
        let currentGroup = accountGroups.find(g => g.id === groupId);
        while (currentGroup?.parent_id) {
          ancestorSet.add(currentGroup.parent_id);
          currentGroup = accountGroups.find(g => g.id === currentGroup!.parent_id);
        }
      });

      result = result.filter(g => filterSet.has(g.id) || ancestorSet.has(g.id));
    }

    return result;
  }, [accountGroups, filteredAccountTypes, filterAccountNature, filterAccountTypeIds, filterAccountGroupIds]);

  // Apply filters to accounts
  const filteredAccounts = useMemo(() => {
    let result = accounts;

    // Filter by inactive
    if (!showInactiveAccounts) {
      result = result.filter(a => a.is_active !== false);
    }

    // Filter by specific account IDs
    if (filterAccountIds && filterAccountIds.length > 0) {
      result = result.filter(a => filterAccountIds.includes(a.id));
    }

    // Filter by group
    const validGroupIds = new Set(filteredAccountGroups.map(g => g.id));
    result = result.filter(a => validGroupIds.has(a.account_group_id));

    return result;
  }, [accounts, filteredAccountGroups, filterAccountIds, showInactiveAccounts]);

  // Build tree nodes
  const allNodes = useMemo(() => {
    if (!filteredAccountTypes.length || !filteredAccountGroups.length) {
      return [];
    }

    return buildTreeNodesFromLocal(filteredAccountTypes, filteredAccountGroups, filteredAccounts);
  }, [filteredAccountTypes, filteredAccountGroups, filteredAccounts]);

  // Apply search filter
  const searchFilteredNodes = useMemo(() => {
    if (!searchQuery.trim()) {
      return allNodes;
    }
    return filterNodesBySearchLocal(allNodes, searchQuery, showInactiveAccounts);
  }, [allNodes, searchQuery, showInactiveAccounts]);

  // Compute expanded IDs including search-related expansions
  const effectiveExpandedIds = useMemo(() => {
    if (!searchQuery.trim()) {
      return expandedIds;
    }

    // When searching, auto-expand ancestors of matching nodes
    const searchExpanded = new Set(expandedIds);
    searchFilteredNodes.forEach(node => {
      if (node.nodeType === 'account') {
        const ancestors = getAncestorIds(node.id, allNodes);
        ancestors.forEach(id => searchExpanded.add(id));
      }
    });

    return searchExpanded;
  }, [expandedIds, searchQuery, searchFilteredNodes, allNodes]);

  // Get visible nodes
  const visibleNodes = useMemo(() => {
    return getVisibleNodes(searchFilteredNodes, effectiveExpandedIds);
  }, [searchFilteredNodes, effectiveExpandedIds]);

  // Toggle expand/collapse
  const toggleExpand = useCallback((nodeId: string) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  // Expand to show a specific node
  const expandToNode = useCallback((nodeId: string) => {
    const ancestors = getAncestorIds(nodeId, allNodes);
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      ancestors.forEach(id => newSet.add(id));
      return newSet;
    });
  }, [allNodes]);

  // Refresh data
  const refreshData = useCallback(() => {
    loadLocalData();
  }, [loadLocalData]);

  // Sync data wrapper
  const syncData = useCallback(async () => {
    await syncDataFn();
  }, [syncDataFn]);

  const isLoadingInitial = isLoading || !isIndexedDBInitialized;

  return {
    visibleNodes,
    allNodes,
    isLoading: isLoadingInitial,
    isLoadingInitial,
    error,
    expandedIds: effectiveExpandedIds,
    toggleExpand,
    expandToNode,
    searchQuery,
    setSearchQuery,
    selectedAccount,
    needsSync,
    isSyncing,
    syncData,
    refreshData,
  };
}
